import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  CalendarCheck,
  Mail,
  Search,
  BrainCircuit,
  Users,
  Share2,
  Check,
} from "lucide-react";
import { BOOKING_URL } from "@/lib/api";
import { Container, Section, Eyebrow, Reveal, MaskedLines, GlowOrb, Marquee, goldBtn, ghostBtn } from "@/components/kit";
import { RoiEstimator } from "@/sections/services/RoiEstimator";
import { CtaBand } from "@/components/CtaBand";
import { cn } from "@/lib/utils";

const stack = [
  "Apollo",
  "Clay",
  "Instantly",
  "Smartlead",
  "LinkedIn Sales Nav",
  "HubSpot",
  "Salesforce",
  "Clearbit",
];

const blocks = [
  {
    icon: CalendarCheck,
    title: "Appointment Setting",
    tag: "The core service",
    desc: "Our flagship. Qualified, ICP-matched meetings booked directly onto your calendar — you only pay for the ones that count.",
    points: ["Parameter-based qualification", "Calendar hand-off & reminders", "Show-rate management"],
  },
  {
    icon: Mail,
    title: "Email Campaigns",
    tag: "Cold outbound",
    desc: "Deliverability-first cold email at scale. Warmed domains, tested copy, and reply-rate optimization built in.",
    points: ["Warmed sending infrastructure", "A/B tested sequences", "Inbox-placement monitoring"],
  },
  {
    icon: Search,
    title: "ICP Research",
    tag: "Intelligence",
    desc: "Deep account research and enrichment so every message lands with the right person, at the right company, with the right angle.",
    points: ["TAM mapping & scoring", "Contact enrichment", "Messaging research"],
  },
  {
    icon: BrainCircuit,
    title: "AI Intent Signals",
    tag: "Timing",
    desc: "Surface in-market accounts using real-time signals — hiring, funding, tech changes and engagement — so you reach buyers when they're ready.",
    points: ["Real-time signal tracking", "Prioritized account queues", "Trigger-based outreach"],
  },
  {
    icon: Users,
    title: "SDR as a Service",
    tag: "Team",
    desc: "A trained outbound team plugged into your motion — the output of a full SDR bench without the hiring, ramp or overhead.",
    points: ["Dedicated outbound reps", "Fully managed workflow", "Transparent reporting"],
  },
  {
    icon: Share2,
    title: "Multi-Channel Outreach",
    tag: "Orchestration",
    desc: "Email, LinkedIn and phone woven into one coherent sequence so prospects experience a consistent, high-converting journey.",
    points: ["Cross-channel sequencing", "Human + AI touchpoints", "Unified conversation tracking"],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-20 md:pt-44 md:pb-24">
        <GlowOrb className="left-1/2 top-0 h-[520px] w-[720px] -translate-x-1/2 opacity-80" />
        <div className="absolute inset-0 -z-10 grain opacity-[0.035]" />
        <Container>
          <Eyebrow>The Engine</Eyebrow>
          <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tighter leading-[0.98] text-white">
            <MaskedLines lines={["Every system we run,", "engineered for"]} delay={0.1} />
            <MaskedLines
              lines={["qualified pipeline"]}
              lineClassName="text-gold text-glow-gold"
              delay={0.1 + 2 * 0.11}
            />
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-white/65"
          >
            Six connected services power Appointment Lab. Run them individually or as a full
            outbound engine — all measured against one metric: qualified meetings.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className={goldBtn}>
              Book A Discovery Call
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <Link to="/contact" className={ghostBtn}>
              Get Qualified Meetings
            </Link>
          </motion.div>
        </Container>
      </section>

      {/* Tech stack strip */}
      <div data-testid="tech-stack-strip" className="relative border-y border-white/10 bg-navy-800/40 py-8">
        <Container className="mb-6">
          <p className="text-center font-mono-jb text-xs uppercase tracking-[0.24em] text-white/40">
            Powered by a modern outbound stack
          </p>
        </Container>
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 md:w-40 bg-gradient-to-r from-navy-900 to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 md:w-40 bg-gradient-to-l from-navy-900 to-transparent z-10" />
        <Marquee
          items={stack}
          itemClassName="font-display text-lg md:text-2xl font-medium text-white/55"
        />
      </div>

      {/* Service blocks (alternating) */}
      <Section>
        <Container>
          <div className="space-y-16 md:space-y-24">
            {blocks.map((b, i) => (
              <Reveal key={b.title}>
                <div
                  className={cn(
                    "grid gap-8 lg:grid-cols-2 lg:items-center",
                    i % 2 === 1 && "lg:[&>*:first-child]:order-2"
                  )}
                >
                  <div>
                    <span className="inline-flex items-center gap-2 rounded-full border border-gold/25 bg-gold/5 px-3 py-1 font-mono-jb text-[11px] uppercase tracking-widest text-gold/80">
                      {b.tag}
                    </span>
                    <div className="mt-5 flex items-center gap-4">
                      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gold text-navy-900">
                        <b.icon className="h-6 w-6" />
                      </span>
                      <h3 className="font-display text-2xl md:text-3xl font-semibold text-white">
                        {b.title}
                      </h3>
                    </div>
                    <p className="mt-5 max-w-lg text-white/60 text-base md:text-lg leading-relaxed">
                      {b.desc}
                    </p>
                    <ul className="mt-7 grid gap-3 sm:grid-cols-2">
                      {b.points.map((p) => (
                        <li key={p} className="flex items-center gap-2.5 text-sm text-white/75">
                          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gold/15 text-gold">
                            <Check className="h-3 w-3" strokeWidth={3} />
                          </span>
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="relative">
                    <div className="pointer-events-none absolute inset-0 gold-radial opacity-60 blur-xl" />
                    <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-navy-800 p-10 md:p-14">
                      <span className="font-mono-jb text-7xl md:text-8xl font-semibold text-white/[0.06]">
                        0{i + 1}
                      </span>
                      <div className="mt-2 flex items-center justify-center">
                        <b.icon className="h-24 w-24 text-gold/80" strokeWidth={1} />
                      </div>
                      <div className="mt-6 h-px w-full bg-white/10" />
                      <p className="mt-5 text-center font-mono-jb text-xs uppercase tracking-widest text-white/40">
                        {b.title} · Appointment Lab
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      <RoiEstimator />
      <CtaBand />
    </div>
  );
}
